import { Box, Button, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AttestedDocument } from "../../models/attestedDocument";

const PDF = "application/pdf";

const FORMAT_NAMES: Record<string, string> = {
  [PDF]: "PDF",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "text/csv": "CSV",
};

const EXTENSIONS: Record<string, string> = {
  [PDF]: "pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "text/csv": "csv",
};

const toBlob = (contentBase64: string, mimeType: string): Blob => {
  const binary = atob(contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
};

// The stored bytes are shown as they are, never re-rendered from the figures.
// A page that redrew the document could differ from the paper and make a
// genuine copy look altered.
const StoredDocument = ({ attested }: { attested: AttestedDocument }) => {
  const { t } = useTranslation();
  const [objectUrl, setObjectUrl] = useState<string>("");

  const mimeType = attested.mime_type ?? "";
  const formatName = FORMAT_NAMES[mimeType] ?? mimeType;
  const extension = EXTENSIONS[mimeType] ?? "bin";
  const displayable = mimeType === PDF;

  const blob = useMemo(() => {
    if (!attested.content_base64 || !mimeType) return null;
    try {
      return toBlob(attested.content_base64, mimeType);
    } catch {
      return null;
    }
  }, [attested.content_base64, mimeType]);

  useEffect(() => {
    if (!blob) return undefined;

    const url = URL.createObjectURL(blob);
    setObjectUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [blob]);

  // Nothing is painted until the object URL exists. Rendering the frame a
  // moment early would put an empty src on the iframe and an empty href on
  // the download, which the browser treats as a re-request of the page.
  if (!blob || !objectUrl) return null;

  return (
    <Box
      sx={theme => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Box
        sx={theme => ({
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          p: "12px 16px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
        })}
      >
        <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
          {t("verify.document.heading")}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          href={objectUrl}
          download={`${attested.reference}.${extension}`}
        >
          {t("verify.document.download")}
        </Button>
      </Box>

      {displayable ? (
        <>
          <Box
            component="iframe"
            title={t("verify.document.frame_title")}
            src={objectUrl}
            sx={{ display: "block", width: "100%", height: "720px", border: 0 }}
          />
          {/*
            When the embedded PDF fails to render, the message the reader sees
            is the BROWSER's own ("Failed to load PDF document. / Reload") --
            it lives inside the iframe, is English in every locale, and is not
            reachable from this document, so it cannot be translated and its
            failure cannot be detected from here either.

            So this line is shown unconditionally rather than on an error we
            cannot observe, and it carries BOTH languages in a single string:
            a French reader stranded on an English browser dialog still reads
            an instruction they understand. The same value is stored under
            both locales on purpose -- it is not a missing translation.
          */}
          <Typography
            sx={theme => ({
              p: "10px 16px",
              fontSize: "12px",
              lineHeight: 1.5,
              color: theme.palette.text.secondary,
              borderTop: `1px solid ${theme.palette.divider}`,
            })}
          >
            {t("verify.document.viewer_fallback")}
          </Typography>
        </>
      ) : (
        <Typography
          sx={theme => ({
            p: "22px",
            fontSize: "14px",
            lineHeight: 1.6,
            color: theme.palette.text.secondary,
          })}
        >
          {t("verify.document.not_displayable", { format: formatName })}
        </Typography>
      )}
    </Box>
  );
};

export default StoredDocument;
